import { useRef, useState, type ReactNode } from "react"
import { Copy, MoreHorizontal } from "lucide-react"
import { copyText } from "../../lib/utils/clipboard"
import { APP_MARKET_LINKS } from "../../data/appMarketLinks"

/**
 * App 下载落地页（SPRINT_278 T-7525；SPRINT_520 T-9325 简化 + 渠道判定修正）。
 *
 * 分发事实（勿改文案前提，2026-10-11 SPRINT_758 T-11776 修订）：
 * - 对外分发口径 = 应用市场（iOS 去 App Store、Android 去应用宝），
 *   链接单源在 `src/data/appMarketLinks.ts`（当前为市场地址，上架后换详情页）；
 * - 页面不再放置内测 OTA / staging 链接（河 2026-10-11：页面不要有 staging 相关地址）；
 *   内测设备安装入口（zyctb.com/ota/）只供内部测试使用，不进本页。
 *
 * T-9325（⑫ 河拍板「一句话 + 一个图标」）：
 * - webview 里的下载引导收敛为「一个图标 + 一句话」，撤掉重复的长按钮文案
 *   （聊天 H5 的「在浏览器打开」会带着当前网址重开本页，复制按钮是冗余路径）；
 * - 渠道判定补「小程序 web-view」一档：它同样是微信 webview（UA 含
 *   MicroMessenger），旧顺序把它并进聊天分支，给出的「右上角 ⋯」指引针对聊天
 *   H5 菜单；小程序 web-view 的胶囊菜单官方文档未承诺「在浏览器打开」，
 *   故该档只讲官方保证的事（复制网址出去打开）；
 * - 复制改双通道 + 失败可见（详见 lib/utils/clipboard.ts）。
 */

type Channel = "miniprogram" | "wechat" | "ios" | "android" | "desktop"
type CopyState = "idle" | "copied" | "failed"

declare global {
  interface Window {
    /** 微信客户端在 web-view 内注入；小程序 web-view 下为 "miniprogram" */
    __wxjs_environment?: string
  }
}

/**
 * 渠道判定。`wxjsEnvironment` 传 `window.__wxjs_environment`（可缺省）。
 *
 * 小程序 web-view 必须排在 MicroMessenger 之前：它的 UA 同时含
 * MicroMessenger，排在后面就永远命中不到。
 * 判据取微信官方 web-view 文档给的两个信号（本页不引 JSSDK，故不用
 * `wx.miniProgram.getEnv`）：UA 含 `miniProgram`（微信 7.0.0+）、
 * 环境变量 `__wxjs_environment === "miniprogram"`。
 */
function detectChannel(ua: string, wxjsEnvironment?: string): Channel {
  if (wxjsEnvironment === "miniprogram" || /miniProgram/i.test(ua)) return "miniprogram"
  if (/MicroMessenger/i.test(ua)) return "wechat"
  if (/Android/i.test(ua)) return "android"
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && "ontouchend" in document)) {
    return "ios"
  }
  return "desktop"
}

function copyButtonLabel(state: CopyState, idle: string, failed: string): string {
  if (state === "copied") return "已复制"
  if (state === "failed") return failed
  return idle
}

/** webview 内的下载引导：一个图标 + 一句话（T-9325 ⑫ 口径的唯一落点） */
function GuideCard({
  icon,
  sentence,
  children,
}: {
  icon: ReactNode
  sentence: string
  children?: ReactNode
}) {
  return (
    <section className="paper-surface rounded-lg border border-hairline p-6 sm:p-8 text-center">
      <div className="flex justify-center text-primary" aria-hidden="true">
        {icon}
      </div>
      <p className="mt-4 text-heading-sm">{sentence}</p>
      {children}
    </section>
  )
}

export function DownloadPage() {
  const channel = detectChannel(navigator.userAgent, window.__wxjs_environment)
  const [copyState, setCopyState] = useState<CopyState>("idle")
  const urlRef = useRef<HTMLSpanElement>(null)
  const timerRef = useRef<number | undefined>(undefined)

  const handleCopy = async (fallbackNode: HTMLElement | null) => {
    window.clearTimeout(timerRef.current)
    const ok = await copyText(window.location.href, fallbackNode)
    setCopyState(ok ? "copied" : "failed")
    if (ok) {
      timerRef.current = window.setTimeout(() => setCopyState("idle"), 2000)
    }
  }

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center">
        <a href="/" className="flex items-center gap-2">
          <img
            src="/landing/logo.jpeg"
            alt="智因错题本"
            className="w-8 h-8 rounded-md object-cover"
          />
          <span className="text-label text-ink">智因错题本</span>
        </a>
      </header>

      <main className="max-w-md mx-auto px-4 pb-24 pt-8 sm:pt-16">
        <div className="text-center mb-10">
          <h1 className="text-heading mb-3">下载智因错题本 App</h1>
          <p className="text-body text-ink-soft">
            错题拍照收录、AI 训练、复习提醒，学习进度与小程序实时同步。
          </p>
        </div>

        {channel === "wechat" && (
          <GuideCard
            icon={<MoreHorizontal className="h-10 w-10" />}
            sentence="点右上角「···」选「在浏览器打开」就能装"
          />
        )}

        {channel === "miniprogram" && (
          <GuideCard
            icon={<Copy className="h-10 w-10" />}
            sentence="微信里装不了，复制下面的网址到手机浏览器打开"
          >
            <p className="mt-5 text-caption text-ink-soft break-all select-all">
              <span ref={urlRef}>{window.location.href}</span>
            </p>
            <button
              onClick={() => handleCopy(urlRef.current)}
              className="mt-4 w-full min-h-12 rounded-md bg-primary text-primary-foreground text-label px-6 py-3 hover:bg-primary-hover transition-colors"
            >
              {copyButtonLabel(copyState, "复制网址", "复制失败，请长按上面的网址")}
            </button>
          </GuideCard>
        )}

        {channel === "ios" && (
          <section className="space-y-4">
            <a
              href={APP_MARKET_LINKS.ios}
              className="block w-full min-h-12 rounded-md bg-primary text-primary-foreground text-label px-6 py-3 text-center hover:bg-primary-hover transition-colors"
            >
              去 App Store 下载
            </a>
            <p className="text-caption text-ink-soft text-center">
              App 上架后，可在 App Store 搜索「智因错题本」安装；学习数据与微信小程序实时同步。
            </p>
          </section>
        )}

        {channel === "android" && (
          <section className="space-y-4">
            <a
              href={APP_MARKET_LINKS.androidStore}
              className="block w-full min-h-12 rounded-md bg-primary text-primary-foreground text-label px-6 py-3 text-center hover:bg-primary-hover transition-colors"
            >
              去应用宝下载
            </a>
            <p className="text-caption text-ink-soft text-center">
              App 上架后，可在应用宝搜索「智因错题本」安装；也可以先用微信小程序「智因错题本」，功能一样好用。
            </p>
          </section>
        )}

        {channel === "desktop" && (
          <section className="paper-surface rounded-lg border border-hairline p-6 sm:p-8 text-center">
            <h2 className="text-heading-sm mb-3">请用手机打开本页面</h2>
            <p className="text-body text-ink-soft">
              用手机浏览器或微信扫描访问，即可安装 App；也可以先使用微信小程序「智因错题本」。
            </p>
          </section>
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-4 pb-10 text-center">
        <p className="text-caption text-ink-soft/70">
          <a href="/" className="hover:text-ink-soft">返回官网</a>
          <span className="mx-2">·</span>
          <a href="/privacy" className="hover:text-ink-soft">隐私政策</a>
        </p>
      </footer>
    </div>
  )
}
